import { Badge } from '../../ui';
import type { WorkstreamGroup } from '../../features/workstreams/workstreamModel';
import type {
  WorkstreamGrouping,
  WorkstreamSummary,
} from '../../features/workstreams/types';
import { WorkstreamCard } from './WorkstreamCard';

export interface WorkstreamBoardProps {
  groups: WorkstreamGroup[];
  grouping: WorkstreamGrouping;
  currentAlias: string;
  onOpen: (w: WorkstreamSummary) => void;
  onToggleFollow: (w: WorkstreamSummary) => void;
}

/**
 * The "what is everyone working on?" board. Renders one column per group (a
 * stage or an engineer). The group header carries the label + a count; stage
 * groups also show a tone dot. Cards omit whichever facet the board is already
 * grouped by to reduce redundancy.
 */
export function WorkstreamBoard({
  groups,
  grouping,
  currentAlias,
  onOpen,
  onToggleFollow,
}: WorkstreamBoardProps) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {groups.map((group) => (
        <section
          key={group.key}
          className="flex min-h-[8rem] flex-col rounded-2xl border border-surface-border bg-surface-2/50 p-2"
        >
          <header className="flex items-center justify-between gap-2 px-1.5 py-1.5">
            <div className="flex min-w-0 items-center gap-2">
              {grouping === 'stage' && group.tone && (
                <Badge tone={group.tone} dot>
                  {group.label}
                </Badge>
              )}
              {grouping === 'engineer' && (
                <span className="truncate text-xs font-semibold uppercase tracking-wide text-zinc-300">
                  {group.label}
                </span>
              )}
            </div>
            <span className="shrink-0 rounded-full bg-surface-3 px-1.5 text-xs text-zinc-500">
              {group.items.length}
            </span>
          </header>

          <div className="flex flex-1 flex-col gap-2 p-1">
            {group.items.map((w) => (
              <WorkstreamCard
                key={w.id}
                workstream={w}
                currentAlias={currentAlias}
                hideStage={grouping === 'stage'}
                hideEngineer={grouping === 'engineer'}
                onOpen={onOpen}
                onToggleFollow={onToggleFollow}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default WorkstreamBoard;
